/**
 * This file contains the "actions" part of the redux architecture
 * It contains a generic function, fetchAction, that will execute a given promise and dispatch it to the reducer.
 */
import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import i18n from '../utils/i18n';
import UserUtils from '../utils/userUtils';
import { rootTypes, loginTypes, pendingTypes, errorTypes, homeTypes } from './types';
import { paths } from '../routes/paths';

export const types = rootTypes;

export const rootActions = {
    clearRootState: () => ({ type: types.CLEAR_ROOT_STATE }),
};

function errorPayload(error) {
    return {
        success: false,
        error: error,
    };
}

/**
 * Check currentURL and referrer to get the save the redirect into url
 * @param dispatch Dispatch
 * @param direction Base url to redirect
 */
function checkAndRedirect(dispatch: Dispatch, direction: string = paths.login) {
    const currentURL = document.URL;
    const pathnameCurrentURL = new URL(currentURL).pathname;
    let ref = document.referrer;
    ref = ref?.length > 0 ? ref : currentURL;
    const path = new URL(ref).pathname;
    const redirect = path !== direction ? `?redirect=${path}` : '';
    if (!pathnameCurrentURL.startsWith(direction)) {
        dispatch(push(`${direction}${redirect}`));
    }
}

function startPendingAction(dispatch: Dispatch, type: string, parameters: any): void {
    if (type !== homeTypes[type]) {
        dispatch({ type: pendingTypes.START_PENDING_OPERATION, payload: { type, id: parameters?.id } });
    }
}

function stopPendingAction(dispatch: Dispatch, type: string): void {
    if (type !== homeTypes[type]) {
        dispatch({ type: pendingTypes.STOP_PENDING_OPERATION, payload: { type } });
    }
}

/**
 *
 * @param promise
 * The promise to perform.
 * Should resolve a javascript object with a "payload" key. That's what will be sent to the data reducer
 * @param type
 * The action id, declared in Consts
 * @param parameters
 * (Optional) the endpoint parameters, will be passed to the reducer
 * (typically contains the object id)
 * @param options
 * (Optional) Some options for this action, like error/success callbacks
 * @returns
 * A dispatchable action (redux-thunk pattern) that will handle the given promise
 */
export function fetchAction(
    promise,
    type,
    parameters,
    options: {
        clear?: boolean;
        format?: Function;
        success?: Function;
        error?: Function;
    } = {
        clear: false,
        success: () => {},
        error: () => {},
    },
) {
    return (dispatch: Dispatch) => {
        const user = UserUtils.getUser();
        if (!user && type !== loginTypes.LOGIN && type !== loginTypes.LOGIN_ID && type !== loginTypes.RESET_PASSWORD) {
            checkAndRedirect(dispatch);
            return true;
        }
        // START Loading type action
        startPendingAction(dispatch, type, parameters);

        if (options.clear) {
            dispatch({ type, parameters, payload: {} });
        }
        return promise
            .then((response) => {
                let payload = response.payload || {};
                if (options.format) {
                    payload = options.format(payload);
                }

                dispatch({ type, parameters, payload });

                // Stop Loader
                stopPendingAction(dispatch, type);

                if (options.success) {
                    options.success(dispatch, response);
                }
            })
            .catch((response) => {
                // STOP Loading type action
                stopPendingAction(dispatch, type);

                // Error go to login
                if (response.status === 403 || response.status === 401) {
                    if (response.key === 'access_denied') {
                        dispatch({
                            type: homeTypes.REDIRECT_HOME,
                            parameters,
                            payload: errorPayload(response),
                        });
                        dispatch(push(paths.home));
                    } else {
                        // key => outdated_token
                        const payload = errorPayload(response);
                        payload.error.message = i18n.t('common:disconnected');
                        dispatch({
                            type: loginTypes.ERROR_GOTO_LOGIN,
                            parameters,
                            payload: payload,
                        });
                        checkAndRedirect(dispatch);
                    }
                }
                // dispatch Error
                dispatch({
                    type: errorTypes.DISPATCH_ERROR,
                    payload: { errorType: type, ...response.payload, documentType: parameters?.type },
                });

                if (options.error) {
                    options.error(dispatch, response);
                }
            });
    };
}
